import type { PartialUserResponse } from '@kfolks/types'
import { apiClient } from '@/shared/api/api.client'

// ─── Type definitions ───────────────────────────────────────────────────────

export interface IdmRequestRecord {
	requestId: string
	email: string
	action: string
	status: string
	signals: Array<{ severity: string; code: string; message: string; requiresOverride: boolean }>
	confirmationPayload: Record<string, unknown> | null
	executionResult: Record<string, unknown> | null
	expiresAt: string
	confirmedAt: string | null
	completedAt: string | null
	cancelledAt: string | null
	createdAt: string
	updatedAt: string
}

export interface AuditEvent {
	eventId: string
	type: string
	entity: string
	action: string
	source: string
	correlationId: string | null
	userId: string | null
	data: Record<string, unknown>
	timestamp: string
}

export interface IdmAccMappingRecord {
	id: number
	idmGroup: string
	accGroup: string
	tool: string
	roleInTool: string | null
	provisioningMethod: string
	isElevatedAccess: boolean
	approvalRequired: boolean
	approvalOwner: string | null
	highRisk: boolean
	notes: string | null
	createdAt: string
	updatedAt: string
}

export interface IdmConfirmInput {
	requestId: string
	overrideSignals?: string[]
	overrideJustification?: string
}

export interface IdmRequestActionResult {
	requestId: string
	status: string
}

export type IdmAccMappingCreateInput = Omit<IdmAccMappingRecord, 'id' | 'createdAt' | 'updatedAt'>
export type IdmAccMappingUpdateInput = Partial<IdmAccMappingRecord>

// ─── API surface ────────────────────────────────────────────────────────────

export interface UserDisplayRecord {
	userId: string
	displayName?: string
	email?: string
}

export const usersApi = {
	// Users (RBAC)
	listUsers: async (): Promise<PartialUserResponse[]> => {
		const response = await apiClient.get<PartialUserResponse[]>('/users')
		return response.data || []
	},
	updateUser: async (id: string, data: Record<string, unknown>): Promise<void> => {
		await apiClient.put(`/users/${id}`, data)
	},
	deleteUser: async (id: string): Promise<void> => {
		await apiClient.delete(`/users/${id}`)
	},
	/**
	 * Dev-only user impersonation. The backend swaps the `access_token` cookie
	 * server-side and stashes the admin's JWT in `original_access_token`, then
	 * responds 204 with no body. The caller reloads so every hook re-mounts
	 * under the impersonated identity. Backend gate: this endpoint is only
	 * mounted on non-prod stages.
	 */
	devImpersonate: async (userId: string): Promise<void> => {
		await apiClient.post('/auth/dev/impersonate', { userId })
	},
	/**
	 * "Return to self" for an active impersonation. Backend swaps the
	 * `access_token` cookie back to the stashed admin JWT and clears
	 * `original_access_token`. 204 no body — the caller reloads.
	 */
	devStopImpersonating: async (): Promise<void> => {
		await apiClient.post('/auth/dev/stop-impersonating')
	},
	/**
	 * Public display-name lookup for a single user — non-admin reachable
	 * (returns `null` when the userId doesn't resolve to a current user, e.g.
	 * deleted account or system actor without a user row).
	 */
	getUserDisplay: async (userId: string): Promise<UserDisplayRecord | null> => {
		const response = await apiClient.get<UserDisplayRecord | null>(`/users/${userId}/display`)
		return response.data
	},

	// IDM Requests
	listIdmRequests: async (status?: string): Promise<IdmRequestRecord[]> => {
		const params = status ? `?filters[status]=${status}` : ''
		const response = await apiClient.get<IdmRequestRecord[]>(`/employees/idm-requests${params}`)
		return response.data || []
	},
	getIdmRequest: async (requestId: string): Promise<IdmRequestRecord> => {
		const response = await apiClient.get<IdmRequestRecord>(`/employees/idm-requests/${requestId}`)
		return response.data
	},
	confirmIdmRequest: async ({
		requestId,
		overrideSignals,
		overrideJustification,
	}: IdmConfirmInput): Promise<IdmRequestActionResult> => {
		const response = await apiClient.post<IdmRequestActionResult>(`/employees/idm-requests/${requestId}/confirm`, {
			confirmed: true,
			overrideSignals,
			overrideJustification,
		})
		return response.data
	},
	cancelIdmRequest: async (requestId: string): Promise<IdmRequestActionResult> => {
		const response = await apiClient.post<IdmRequestActionResult>(`/employees/idm-requests/${requestId}/cancel`, {})
		return response.data
	},
	getIdmAuditTrail: async (requestId: string): Promise<AuditEvent[]> => {
		const response = await apiClient.get<AuditEvent[]>(
			`/events?filters[entityType]=idm_request&filters[entityId]=${requestId}`,
		)
		return response.data || []
	},

	// IDM ACC Mappings
	listIdmAccMappings: async (): Promise<IdmAccMappingRecord[]> => {
		const response = await apiClient.get<IdmAccMappingRecord[]>('/employees/idm-acc-mappings')
		return response.data || []
	},
	createIdmAccMapping: async (data: IdmAccMappingCreateInput): Promise<IdmAccMappingRecord> => {
		const response = await apiClient.post<IdmAccMappingRecord>('/employees/idm-acc-mappings', data)
		return response.data
	},
	updateIdmAccMapping: async ({
		id,
		data,
	}: {
		id: number
		data: IdmAccMappingUpdateInput
	}): Promise<IdmAccMappingRecord> => {
		const response = await apiClient.put<IdmAccMappingRecord>(`/employees/idm-acc-mappings/${id}`, data)
		return response.data
	},
	deleteIdmAccMapping: async (id: number): Promise<void> => {
		await apiClient.delete(`/employees/idm-acc-mappings/${id}`)
	},
}
